import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../translations';

const SettingsButton = styled(motion.button)`
  background: transparent;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuContainer = styled(motion.div)`
  position: absolute;
  top: 50px;
  right: 0;
  background: ${props => props.theme === 'dark' ? '#1e1e1e' : '#ffffff'};
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, ${props => props.theme === 'dark' ? '0.3' : '0.1'});
  padding: 15px;
  min-width: 220px;
  z-index: 1000;
  border: 1px solid ${props => props.theme === 'dark' ? '#343a40' : '#e9ecef'};
  
  @media (max-width: 768px) {
    position: fixed;
    top: auto;
    bottom: 70px;
    right: 20px;
  }
`;

const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${props => props.theme === 'dark' ? '#343a40' : '#e9ecef'};
`;

const MenuTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const MenuSection = styled.div`
  margin-bottom: 15px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 0.9rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  font-weight: 500;
`;

const ThemeToggleContainer = styled.div`
  display: flex;
  background: ${props => props.theme === 'dark' ? '#121212' : '#f8f9fa'};
  border-radius: 30px;
  padding: 5px;
  position: relative;
`;

const ThemeOption = styled.button`
  background: transparent;
  border: none;
  padding: 8px 15px;
  border-radius: 30px;
  cursor: pointer;
  position: relative;
  z-index: 2;
  color: ${props => props.active 
    ? props.theme === 'dark' ? '#121212' : '#ffffff'
    : props.theme === 'dark' ? '#f8f9fa' : '#333'};
  font-weight: ${props => props.active ? '600' : '400'};
  transition: color 0.3s ease;
  flex: 1;
  text-align: center;
  
  &:hover {
    color: ${props => props.active 
      ? props.theme === 'dark' ? '#121212' : '#ffffff'
      : '#4a6cf7'};
  }
`;

const ThemeIndicator = styled(motion.div)`
  position: absolute;
  width: 50%;
  height: 80%;
  border-radius: 30px;
  background: ${props => props.theme === 'dark' ? '#f8f9fa' : '#4a6cf7'};
  top: 10%;
  left: ${props => props.position === 'light' ? '5px' : '50%'};
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

const LanguageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: ${props => props.active ? 
    props.theme === 'dark' ? 'rgba(74, 108, 247, 0.2)' : 'rgba(74, 108, 247, 0.1)' : 
    'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme === 'dark' ? 'rgba(74, 108, 247, 0.3)' : 'rgba(74, 108, 247, 0.2)'};
  }
`;

const LanguageFlag = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 10px;
  background-image: url(${props => props.flag});
  background-size: cover;
  background-position: center;
`;

const LanguageName = styled.span`
  color: ${props => props.active ? '#4a6cf7' : props.theme === 'dark' ? '#f8f9fa' : '#333'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 0.9rem;
  flex: 1;
  text-align: left;
`;

const LanguageCheck = styled.span`
  color: #4a6cf7;
  margin-left: 10px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SettingsMenu = ({ theme, toggleTheme }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const menuRef = useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Language options with flags
  const languageOptions = [
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/gb.png' },
    { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w40/de.png' }
  ];
  
  // Handle language change
  const handleLanguageChange = (langCode) => {
    if (language !== langCode) {
      toggleLanguage();
    }
  };
  
  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <SettingsButton 
        theme={theme}
        onClick={() => setMenuOpen(!menuOpen)}
        whileTap={{ scale: 0.9 }}
        aria-label="Settings"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </SettingsButton>
      
      <AnimatePresence>
        {menuOpen && (
          <MenuContainer
            theme={theme}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <MenuHeader theme={theme}>
              <MenuTitle theme={theme}>Settings</MenuTitle>
              <CloseButton 
                onClick={() => setMenuOpen(false)}
                theme={theme}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </CloseButton>
            </MenuHeader>
            
            <MenuSection>
              <SectionTitle theme={theme}>Theme</SectionTitle>
              <ThemeToggleContainer theme={theme}>
                <ThemeOption 
                  active={theme === 'light'} 
                  onClick={theme === 'dark' ? toggleTheme : undefined}
                  theme={theme}
                >
                  Light
                </ThemeOption>
                <ThemeOption 
                  active={theme === 'dark'} 
                  onClick={theme === 'light' ? toggleTheme : undefined}
                  theme={theme}
                >
                  Dark
                </ThemeOption>
                <ThemeIndicator 
                  position={theme}
                  theme={theme}
                  layoutId="themeIndicator"
                />
              </ThemeToggleContainer>
            </MenuSection>
            
            <MenuSection>
              <SectionTitle theme={theme}>Language</SectionTitle>
              <LanguageContainer>
                {languageOptions.map((lang) => (
                  <LanguageOption 
                    key={lang.code}
                    active={language === lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    theme={theme}
                  >
                    <LanguageFlag flag={lang.flag} />
                    <LanguageName 
                      active={language === lang.code}
                      theme={theme}
                    >
                      {lang.name}
                    </LanguageName>
                    {language === lang.code && (
                      <LanguageCheck>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </LanguageCheck>
                    )}
                  </LanguageOption>
                ))}
              </LanguageContainer>
            </MenuSection>
          </MenuContainer>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SettingsMenu;
